import { ProductDataProcessor, ProductRenderer, CONSTANTS as SEARCHSPRING_CONSTANTS } from '@theme/searchspring-core';
import { announceRender, trackingParams } from '@theme/searchspring-beacon';
import { consentReady, consentState } from '@theme/searchspring-consent';
import { applyColorOverrides } from '@theme/search-results-renderer';

/**
 * @typedef {Object} SearchspringConfig
 * @property {string} productTypeToUse - Product type filter ("Made to Order" | "Ready to Ship" | "both")
 * @property {string} tags - Search tags for product recommendations
 * @property {string} productId - Specific product ID for recommendations
 * @property {number} limits - Maximum number of recommendations to fetch
 * @property {string} userId - User identifier for personalization
 * @property {string} lastViewedProductsIds - Comma-separated list of viewed product IDs
 *
 * @typedef {Object} SearchspringApiOptions
 * @property {string} [productId] - Specific product ID for recommendations
 * @property {number} [limits] - Maximum number of recommendations to fetch
 * @property {string} [userId] - User identifier for personalization
 * @property {string} [lastViewed] - Comma-separated list of viewed product IDs
 * @property {string[]} [blockedItems] - Product IDs or SKUs the profile must not return
 * @property {string} [cart] - Comma-separated product IDs in the cart, for cart cross-sell profiles
 * @property {Record<string, string[]>} [bgfilter] - Background filters (e.g. { ss_color_code: ['AGV','FRV'] })
 *
 * @typedef {Object} SearchspringPriceRange
 * @property {string | number} size - Size identifier (* for all sizes)
 * @property {number} price - Current price
 * @property {number} compareAtPrice - Original price for comparison
 *
 * @typedef {Record<string, SearchspringPriceRange[]>} SearchspringColorPrices
 * @typedef {Record<string, SearchspringColorPrices>} SearchspringPrices
 *
 * @typedef {Object} ExtendedSearchspringColorOption
 * @property {string} key
 * @property {string} [color]
 * @property {string | null} [label]
 * @property {string[]} [images]
 *
 * @typedef {Object} ExtendedSearchspringProductOptions
 * @property {ExtendedSearchspringColorOption[]} [colors] - Available color options
 * @property {Record<string, number[]>} [sizes] - Size groups organized by color
 * @property {number[]} [allSizes] - All available sizes across colors

 * @typedef {Object} ExtendedSearchspringProduct
 * @property {string} [id] - Product ID
 * @property {string} [title] - Product title
 * @property {string} [handle] - Product handle
 * @property {any} [options] - Raw product options
 * @property {any} [prices] - Processed price data
 * @property {any} [parsedOptions] - Parsed options data
 * @property {ExtendedSearchspringProductOptions} [searchspringOptions] - Searchspring-specific options
 * @property {string} [mfield_searchspring_price] - Raw price metafield value
 */

// Constants
const CONSTANTS = {
  DEFAULT_LIMITS: 8,
  /**
   * Extra collection products requested: one for the page's own product, the
   * rest for cells the API holds back for the collection's inline banners.
   */
  COLLECTION_HEADROOM: 5,
  /** Longest the request waits for the consent API before going without a shopper. */
  CONSENT_WAIT_MS: 1000,
  METAFIELD_NAMESPACE: 'searchspring',
  METAFIELD_KEYS: {
    COLORS: 'colors',
    COLOR_LABELS: 'color_labels',
    COLOR_SIZES: 'color_sizes',
    VARIANTS_GALLERY: 'variants_gallery',
    PRICE: 'price',
    PARENT_PRODUCT: 'parent_product',
    COLOR_PATTERN: 'color-pattern',
  },
  LOCAL_STORAGE_KEYS: {
    VIEWED_PRODUCTS: 'viewedProducts',
  },
};

class SearchspringApiService {
  /** @type {string} */
  siteId;

  /**
   * @param {string} siteId
   */
  constructor(siteId) {
    if (!siteId) {
      throw new Error('SearchspringApiService requires a siteId');
    }
    this.siteId = siteId;
  }

  /**
   * Fetch product recommendations from Searchspring API
   * @param {string} tags - Search tags for recommendations
   * @param {SearchspringApiOptions} [options] - Additional API options
   * @returns {Promise<{ tag: string, responseId: string, products: SearchspringProduct[] }>} The
   *   profile tag and responseId that beacon events correlate on, plus the products.
   */
  async getRecommendations(tags, options = {}) {
    const {
      productId = '',
      limits = CONSTANTS.DEFAULT_LIMITS,
      userId = '',
      lastViewed = '',
      blockedItems = [],
      cart = '',
      bgfilter = {},
    } = options;

    const productParam = productId ? `&product=${encodeURIComponent(productId)}` : '';
    const cartParam = cart ? `&cart=${encodeURIComponent(cart)}` : '';
    const blockedParam = blockedItems.length
      ? `&blockedItems=${encodeURIComponent(blockedItems.join(','))}`
      : '';

    // Build bgfilter query params (e.g. &bgfilter.ss_color_code=AGV&bgfilter.ss_color_code=FRV)
    let bgfilterParams = '';
    for (const [key, values] of Object.entries(bgfilter)) {
      for (const value of values) {
        bgfilterParams += `&bgfilter.${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    }

    const url = `https://${this.siteId}.a.searchspring.io/boost/${this.siteId}/recommend?beacon=true&tags=${encodeURIComponent(tags)}${productParam}${cartParam}&limits=${limits}&shopper=${encodeURIComponent(userId)}&lastViewed=${encodeURIComponent(lastViewed)}${blockedParam}${bgfilterParams}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      const data = await response.json();
      const flattened = data.flat();
      const profile = flattened[0] || {};

      // uid and sku live in `mappings.core`, not in the `attributes` bag — carry
      // both across, since beacon events require uid and sku per result.
      const products = (profile.results || []).map(
        /** @param {{ attributes: any, mappings?: { core?: { uid?: string, sku?: string } } }} product */
        (product) => ({
          ...product.attributes,
          uid: product.mappings?.core?.uid,
          sku: product.mappings?.core?.sku,
        })
      );

      return {
        tag: profile.profile?.tag || '',
        responseId: profile.responseId || '',
        products,
      };
    } catch (error) {
      console.error('Searchspring API error:', error);
      throw error;
    }
  }

  /**
   * Fetch a collection's products, in its collection-page order. These aren't
   * recommendations, so there's no tag or responseId and the beacon reports
   * nothing for them.
   * @param {string} collectionHandle
   * @param {number} limit
   * @returns {Promise<{ tag: string, responseId: string, products: SearchspringProduct[] }>}
   */
  async getCollectionProducts(collectionHandle, limit) {
    const url = `https://${this.siteId}.a.searchspring.io/api/search/category.json?siteId=${this.siteId}&bgfilter.collection_handle=${encodeURIComponent(collectionHandle)}&resultsFormat=native&resultsPerPage=${limit}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    const data = await response.json();

    return { tag: '', responseId: '', products: data.results || [] };
  }
}

class SearchspringRecommendations extends HTMLElement {
  /**
   * Recommendation beacon events are keyed by profile tag as well as
   * responseId; both are read off this element by searchspring-beacon.js.
   * @type {string}
   */
  responseId = '';

  /** @type {string} */
  tag = '';

  #config = {
    productTypeToUse: SEARCHSPRING_CONSTANTS.PRODUCT_TYPES.BOTH,
    tags: '',
    productId: '',
    cartIds: '',
    currentStyle: '',
    limits: CONSTANTS.DEFAULT_LIMITS,
    userId: '',
    lastViewedProductsIds: '',
    promotedColors: '',
    promotedColorsMode: 'filter',
    collection: '',
  };

  // add private site id field
  #siteId = '';

  /** @type {boolean} */
  #isB2B = false;

  /** Counts requests, so a slower earlier answer can't replace a newer one. */
  #requestCount = 0;

  /**
   * Lifecycle method called when element is added to DOM
   */
  async connectedCallback() {
    this.setSettings();

    if (!this.#config.tags) return;
    if (!this.#siteId) {
      console.warn('searchspring-recommendations: missing data-site-id attribute');
      return;
    }

    this.getRecommendations();
  }

  /**
   * Initialize settings from element attributes
   */
  setSettings() {
    this.#config.tags = this.getAttribute('data-tags') || '';
    this.#config.productTypeToUse =
      this.getAttribute('data-product-types') || SEARCHSPRING_CONSTANTS.PRODUCT_TYPES.BOTH;
    this.#config.productId = this.getAttribute('data-product') || '';
    this.#config.cartIds = this.getAttribute('data-cart') || '';
    this.#config.currentStyle = this.getAttribute('data-current-style') || '';
    this.#config.limits = Number(this.getAttribute('data-limits')) || CONSTANTS.DEFAULT_LIMITS;
    this.#config.lastViewedProductsIds = this.getLastViewedProducts(this.#config.currentStyle);
    // read site id from attribute only; no fallback to constants
    this.#siteId = this.getAttribute('data-site-id') || '';
    this.#config.promotedColors = this.getAttribute('data-promoted-colors') || '';
    this.#config.promotedColorsMode = this.getAttribute('data-promoted-colors-mode') || 'filter';
    this.#config.collection = this.getAttribute('data-collection') || '';
    this.#isB2B = this.getAttribute('data-is-b2b') === 'true';
  }

  /**
   * The tracker's userId, sent as `shopper`. It's analytics data, so it's
   * sent only with consent. Before the consent API can answer, an ID stored
   * with consent goes at once (the head prefetch's rule). Otherwise it waits
   * for the API, at most CONSENT_WAIT_MS, and trackingParams() decides: empty
   * if the API refuses, or the visitor has withdrawn since, here or in
   * another tab. Never throws: the recommendations load either way.
   *
   * `lastViewed` follows this answer: it is sent only when the shopper ID is
   * (see getRecommendations()).
   * @returns {Promise<string>} The userId, or '' without consent.
   */
  async getShopperId() {
    try {
      const seed = window.__ssTracking;
      if (seed?.persisted && consentState() === 'unknown') return seed.userId || '';

      await Promise.race([
        consentReady(),
        new Promise((resolve) => setTimeout(resolve, CONSTANTS.CONSENT_WAIT_MS)),
      ]);
      // Empty without analytics consent.
      return trackingParams().userId || '';
    } catch {
      return '';
    }
  }

  /**
   * Get last viewed products from localStorage
   * @param {string} [excludeStyle] - Style code to exclude (e.g. the current product)
   * @returns {string} Comma-separated product IDs
   */
  getLastViewedProducts(excludeStyle = '') {
    try {
      const lastViewedData = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_KEYS.VIEWED_PRODUCTS);
      if (!lastViewedData || !lastViewedData.length) return '';
      /** @type {string[]} */
      const ids = JSON.parse(lastViewedData);
      const filtered = excludeStyle ? ids.filter((id) => id !== excludeStyle) : ids;
      return filtered.join(',');
    } catch (error) {
      console.warn('Failed to parse last viewed products from localStorage:', error);
      return '';
    }
  }

  /**
   * Fetch and render product recommendations
   */
  async getRecommendations() {
    // The cart page asks again on every cart change; only the latest answer counts.
    const request = ++this.#requestCount;
    try {
      const apiService = new SearchspringApiService(this.#siteId);

      this.#config.userId = await this.getShopperId();

      const { productId, limits, collection } = this.#config;

      /** @type {SearchspringApiOptions} */
      const apiOptions = {
        productId,
        limits,
        // Never recommend the style the shopper is already looking at.
        blockedItems: productId ? [productId] : [],
        cart: this.#config.cartIds,
        userId: this.#config.userId,
        // The styles this visitor has looked at are behavioural data, so they
        // leave the device only with the shopper ID they'd be joined to —
        // which means only with analytics consent. The list itself is the
        // theme's own recently-viewed feature and is kept either way.
        lastViewed: this.#config.userId ? this.#config.lastViewedProductsIds : '',
      };

      // Pass promoted colors as bgfilter to exclude non-matching products
      const promotedColorsList = this.#config.promotedColors
        ? this.#config.promotedColors.split(',').map((c) => c.trim()).filter(Boolean)
        : [];
      if (promotedColorsList.length > 0) {
        apiOptions.bgfilter = { 'ss_color_code': promotedColorsList };
      }

      const { tag, responseId, products } = collection
        ? await apiService.getCollectionProducts(collection, limits + CONSTANTS.COLLECTION_HEADROOM)
        : await apiService.getRecommendations(this.#config.tags, apiOptions);
      if (request !== this.#requestCount) return;
      this.responseId = responseId;
      this.tag = tag;

      const productProcessor = new ProductDataProcessor();
      const formattedProducts = productProcessor.formatProducts(
        // A collection includes the page's own product; recommendations already block it.
        products.filter((product) => product.uid !== productId).slice(0, limits),
        this.#config.productTypeToUse
      );
      if (this.#isB2B) {
        applyColorOverrides(formattedProducts, null, productProcessor, true);
      }
      const productsWithOptions = productProcessor.mapProductOptions(formattedProducts);

      if (productsWithOptions.length === 0) {
        this.style.display = 'none';
        return;
      }
      // An earlier request may have hidden the carousel.
      this.style.display = '';

      const renderer = new ProductRenderer(this);
      /** @type {{ promotedColors?: string[], promotedColorsMode?: string, isB2B?: boolean }} */
      const renderOptions = {};
      if (promotedColorsList.length > 0) {
        renderOptions.promotedColors = promotedColorsList;
        renderOptions.promotedColorsMode = this.#config.promotedColorsMode;
      }
      if (this.#isB2B) {
        renderOptions.isB2B = true;
      }
      await renderer.renderProducts(/** @type {SearchspringProduct[]} */ (productsWithOptions), true, false, renderOptions);

      // The carousel renders searchspring-product-card elements, which register
      // themselves as impression targets on mount.
      announceRender(this);
    } catch (error) {
      console.error('Failed to get recommendations:', error);
      if (request === this.#requestCount) this.style.display = 'none';
    }
  }

  /**
   * Lifecycle method called when element is removed from DOM
   */
  disconnectedCallback() {}
}

if (!customElements.get('searchspring-recommendations')) {
  customElements.define('searchspring-recommendations', SearchspringRecommendations);
}
